import { useMemo } from "react";
import { IconPlus } from "@tabler/icons-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useBuildsByEvent } from "@/api/hooks/use-builds";
import { asEventId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";
import type { AdminEventsActions } from "@/hooks/use-admin-events-actions";

type BoatWithTeam = components["schemas"]["BoatWithTeam"];

interface Props {
  eventId: string;
  boats: BoatWithTeam[];
  actions: AdminEventsActions;
}

export function EventBuildsPanel({ eventId, boats, actions }: Props) {
  const id = asEventId(eventId);
  const { data: builds, isLoading } = useBuildsByEvent(id);

  const missing = useMemo(() => {
    if (!builds) return [];
    const built = new Set(builds.map((b) => b.boat_id));
    return boats
      .filter((boat) => !built.has(boat.id))
      .sort((a, b) =>
        (a.team?.name ?? a.boat_code).localeCompare(b.team?.name ?? b.boat_code),
      );
  }, [builds, boats]);

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-9 w-full rounded-md" />
        ))}
      </div>
    );
  }

  const sorted = [...(builds ?? [])].sort((a, b) =>
    (a.team_name ?? a.boat_code ?? "").localeCompare(
      b.team_name ?? b.boat_code ?? "",
    ),
  );

  return (
    <div className="px-4 py-3 space-y-3">
      <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.06em]">
        Builds ({sorted.length})
      </div>

      {sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">No builds yet.</p>
      ) : (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {sorted.map((build) => (
            <div
              key={build.id}
              className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-1.5 text-sm"
            >
              <span className="font-medium">
                {build.team_name ?? build.boat_code ?? "—"}
              </span>
              <span className="text-muted-foreground">{build.boat_code}</span>
              {build.signed_off_at && (
                <span className="ml-auto text-[11px] font-medium text-green-600 dark:text-green-500">
                  Signed off
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {missing.length > 0 && (
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {missing.length} team{missing.length === 1 ? "" : "s"} without a build
            </span>
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
              disabled={actions.isSaving}
              onClick={() =>
                actions.handleGenerateBuilds(
                  id,
                  missing.map((b) => b.id),
                )
              }
            >
              Add all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {missing.map((boat) => (
              <button
                key={boat.id}
                type="button"
                disabled={actions.isSaving}
                onClick={() => actions.handleGenerateBuilds(id, [boat.id])}
                className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs hover:bg-accent disabled:opacity-50"
              >
                <IconPlus className="h-3 w-3" />
                {boat.team?.name ?? boat.boat_code}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
